import qs from 'qs';

import {
  ChangeLogFilters,
  ClusterOffersResponse,
  ClusteringRequest,
  ClusteringRunResponse,
  ClusteringsResponse,
  ClustersResponse,
  DynamicPricingExclusionConfig,
  DynamicPricingExclusionsResponse,
  DynamicPricingSurchargeEstimateParams,
  DynamicPricingSurchargeEstimates,
  EligibleSurchargeOffersResponse,
  HurdleHeatmap,
  HurdleHeatmapCatalog,
  LereAbandonedCarts,
  LerePaginatedResponse,
  LereToursPricingApplyRequest,
  LereToursPricingHistoryParams,
  LereToursPricingHistoryRecord,
  LereToursPricingSuggestion,
  LereToursPricingSuggestionsParams,
  OfferRecommendationsResponse,
  SurchargeSuggestionDB,
  SurchargeSuggestionFilters,
} from '~/components/LERE/types';

import { json_headers, request } from './common';

function basePath() {
  return window.configs.API_HOST + '/api/recommendation';
}

type RequestOptions = {
  signal?: AbortSignal;
};

function normalizePaginatedResponse<T>(response: {
  result: Array<T>;
  total: number;
  timestamp?: number;
}): LerePaginatedResponse<T> {
  return {
    result: response.result,
    total: response.total,
    timestamp: response.timestamp,
  };
}

function sendTPFMTestEmail(campaign: string, emailRecipient: string, reuseLastRecipientEmail: boolean) {
  return request(`${basePath()}/operations/test_trigger`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      campaign: campaign,
      recipient: emailRecipient,
      reuse_last_recipient_email: reuseLastRecipientEmail,
    }),
  });
}

function clearUserInteractions(params: { userId?: string; domainUserId?: string }) {
  return request(`${basePath()}/user-interactions/clear`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      le_user_id: params.userId,
      domain_user_id: params.domainUserId,
    }),
  });
}

function getUserInteractions(params: { userId?: string; domainUserId?: string }) {
  const queryParams = {
    le_user_id: params.userId,
    domain_user_id: params.domainUserId,
  } as Record<string, string | undefined>;

  return request(`${basePath()}/user-interactions/all?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function getChangeLog(page: number, limit = 5, filters?: ChangeLogFilters) {
  const queryParams = {
    page,
    limit,
    property_name: filters?.propertyName,
    room_type_name: filters?.roomTypeName,
    actioned_at_start: filters?.actionedDateRange?.start,
    actioned_at_end: filters?.actionedDateRange?.end,
    status: filters?.status,
    enforce_room_type_granularity: filters?.enforceRoomTypeGranularity,
    only_charters: filters?.onlyCharters,
    suggestion_type: filters?.suggestionType,
  };

  return request(`${basePath()}/dynamic_pricing/?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

function getLatestSurchargeSuggestions(page: number, limit = 10, filters?: SurchargeSuggestionFilters) {
  const queryParams = {
    page,
    limit,
    aggregate: filters?.aggregate,
    offer_id: filters?.offerIdEquals,
    property_name: filters?.propertyNameLike,
    room_type_name: filters?.roomTypeNameLike,
    internal_surcharge: filters?.internalSurchargeEquals,
    sales_gte: filters?.salesGte,
    allocated_gte: filters?.allocatedGte,
    suggestion_type: filters?.suggestionType,
    confidence: filters?.confidence,
    sortby: filters?.sortBy,
    ascending: filters?.ascending,
    only_charters: filters?.onlyCharters,
  };

  // Map the sortby values to the correct API parameters
  if (queryParams.sortby === 'actioned_start_date') {
    queryParams.sortby = 'start_date';
  } else if (queryParams.sortby === 'actioned_end_date') {
    queryParams.sortby = 'end_date';
  }

  return request(`${basePath()}/dynamic_pricing/latest?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
  });
}

async function getToursPricingSuggestions(
  params: LereToursPricingSuggestionsParams,
  options?: RequestOptions,
): Promise<LerePaginatedResponse<LereToursPricingSuggestion>> {
  const queryParams = {
    page: params.page,
    limit: params.limit,
    offer_bk: params.offer_bk,
    offer_salesforce_bk: params.offer_salesforce_bk,
    suggested_action_type: params.suggested_action_type,
  };

  const response = await request(`${basePath()}/tours_dynamic_pricing?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });

  return normalizePaginatedResponse<LereToursPricingSuggestion>(response);
}

async function getToursPricingHistory(
  params: LereToursPricingHistoryParams,
  options?: RequestOptions,
): Promise<LerePaginatedResponse<LereToursPricingHistoryRecord>> {
  const queryParams = {
    page: params.page,
    limit: params.limit,
    offer_bk: params.offer_bk,
    offer_salesforce_bk: params.offer_salesforce_bk,
    suggested_action_type: params.suggested_action_type,
    status: params.status,
    created_at_start: params.created_at_start,
    created_at_end: params.created_at_end,
  };

  const response = await request(`${basePath()}/tours_dynamic_pricing/history?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });

  return normalizePaginatedResponse<LereToursPricingHistoryRecord>(response);
}

async function applyToursPricingSuggestion(body: LereToursPricingApplyRequest): Promise<void> {
  await request(`${basePath()}/tours_dynamic_pricing/`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

function createSurchargeSuggestion(body: Omit<SurchargeSuggestionDB, 'id'>): Promise<{ job_id: string }> {
  return request(`${basePath()}/dynamic_pricing`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

async function getAutoYieldStatus(options?: RequestOptions): Promise<{ status: boolean }> {
  const response = await request(`${basePath()}/dynamic_pricing/auto_yield_status`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });
  return response || { status: false };
}

function getSurchargeJobStatus(
  jobId: string,
  options?: RequestOptions,
): Promise<{ status: 'pending' | 'success' | 'failure' }> {
  return request(`${basePath()}/dynamic_pricing/surcharge/status/${encodeURIComponent(jobId)}`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });
}

async function getDynamicPricingExclusions(options?: RequestOptions): Promise<Array<DynamicPricingExclusionConfig>> {
  const response: DynamicPricingExclusionsResponse = await request(`${basePath()}/dynamic_pricing/exclusion`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });

  return response.result || [];
}

async function getDynamicPricingSurchargeEstimates(
  params: DynamicPricingSurchargeEstimateParams,
  options?: RequestOptions,
): Promise<DynamicPricingSurchargeEstimates> {
  const queryParams = {
    offer_id: params.offerId,
    check_in_date: params.checkInDate,
    check_out_date: params.checkOutDate,
  };

  return await request(`${basePath()}/dynamic_pricing/estimate_surcharge_amount?${qs.stringify(queryParams)}`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });
}

async function getEligibleSurchargeOffers(options?: RequestOptions): Promise<EligibleSurchargeOffersResponse> {
  return await request(`${basePath()}/dynamic_pricing/eligible_surcharge_offers`, {
    method: 'GET',
    headers: json_headers,
    signal: options?.signal,
  });
}

async function upsertDynamicPricingExclusion(body: DynamicPricingExclusionConfig): Promise<void> {
  await request(`${basePath()}/dynamic_pricing/exclusion`, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

async function deleteDynamicPricingExclusion(entityId: string): Promise<void> {
  await request(`${basePath()}/dynamic_pricing/exclusion/${encodeURIComponent(entityId)}`, {
    method: 'DELETE',
    headers: json_headers,
  });
}

function getAbandonedCarts(params: { userId: string; brand: string; region: string }): Promise<LereAbandonedCarts> {
  const { userId, brand, region } = params;

  const queryParams = {
    region,
    brand,
    limit: 50,
    categories: 'sticky_abandoned_carts',
    le_user_id: userId,
  };
  return request(`${basePath()}/enquiry?${qs.stringify(queryParams)}`, {
    method: 'POST',
    body: JSON.stringify({ sticky_abandoned_carts: {} }),
    headers: json_headers,
  }).then((response) => response.result.sticky_abandoned_carts);
}

/**
 * Get clusters for map visualization.
 *
 * @param params.run_id - Explicit run ID to fetch (optional)
 * @param params.as_of - ISO date string (YYYY-MM-DD) to get the most recent run as of that date (optional)
 * @returns Clusters data or null if not found
 */
async function getClusters(params?: { run_id?: string; as_of?: string }): Promise<ClustersResponse> {
  const queryParams = {
    run_id: params?.run_id,
    as_of: params?.as_of,
  };

  const queryString = qs.stringify(queryParams, { skipNulls: true });
  const url = `${basePath()}/admin/clusterings/clusters${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

/**
 * List available clusterings.
 *
 * @param limit - Maximum number of clusterings to return. If not provided, returns all.
 * @returns List of clusterings
 */
async function getClusterings(limit?: number): Promise<ClusteringsResponse> {
  const queryParams = { limit };
  const queryString = qs.stringify(queryParams, { skipNulls: true });
  const url = `${basePath()}/admin/clusterings${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

/**
 * Get offer_id to cluster_id mapping for a clustering run.
 *
 * @param params.run_id - Explicit run ID to fetch (optional). If not provided, returns the latest.
 * @returns Offer-to-cluster assignments
 */
async function getClusterOffers(params?: { run_id?: string }): Promise<ClusterOffersResponse> {
  const queryParams = {
    run_id: params?.run_id,
  };

  const queryString = qs.stringify(queryParams, { skipNulls: true });
  const url = `${basePath()}/admin/clusterings/offers${queryString ? `?${queryString}` : ''}`;

  return request(url, {
    method: 'GET',
    headers: json_headers,
  });
}

/**
 * Get two towers offer-to-offer recommendations via the enquiry endpoint.
 *
 * @param params.offer_id - The offer ID to get recommendations for (required)
 * @param params.region - Region code (optional, defaults to AU)
 * @param params.limit - Max recommendations to return (optional)
 * @returns Offer recommendations with scores
 */
async function getOfferRecommendations(params: {
  offer_id: string;
  region?: string;
  limit?: number;
}): Promise<OfferRecommendationsResponse> {
  const queryParams = {
    categories: 'two_towers',
    region: params.region || 'AU',
    request_mode: 'offer_to_offer',
    use_clusters: true,
    limit: params.limit,
  };

  const queryString = qs.stringify(queryParams, { skipNulls: true });
  const url = `${basePath()}/enquiry?${queryString}`;

  const response = await request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify({
      two_towers: {
        offer_id: params.offer_id,
      },
    }),
  });

  // The enquiry endpoint returns { result: { two_towers: { result: [...] } } }
  const twoTowersResult = response?.result?.two_towers;
  if (response?.result && !twoTowersResult) {
    console.warn('Unexpected recommendation response shape', Object.keys(response.result));
  }
  const recommendations = twoTowersResult?.result ?? [];
  return {
    offer_id: params.offer_id,
    recommendations,
    count: recommendations.length,
  };
}

async function runClustering(body: ClusteringRequest = {}): Promise<ClusteringRunResponse> {
  const url = `${basePath()}/admin/clusterings`;

  return request(url, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
}

async function listHurdleHeatmaps(offerId?: string): Promise<HurdleHeatmapCatalog> {
  const queryString = qs.stringify({ offer_id: offerId || undefined }, { skipNulls: true });
  const url = `${basePath()}/admin/hotel-sellout/hurdle-heatmaps${queryString ? `?${queryString}` : ''}`;
  const response: { offer_ids: Array<string>; count: number } = await request(url, {
    method: 'GET',
    headers: json_headers,
  });

  return {
    offerIds: response.offer_ids,
    count: response.count,
  };
}

async function getHurdleHeatmap(offerId: string): Promise<HurdleHeatmap> {
  const response: {
    offer_id: string;
    generated_at: string;
    pdf_url: string;
    expires_at: string;
  } = await request(`${basePath()}/admin/hotel-sellout/hurdle-heatmaps/${encodeURIComponent(offerId)}`, {
    method: 'GET',
    headers: json_headers,
  });

  return {
    offerId: response.offer_id,
    generatedAt: response.generated_at,
    pdfUrl: response.pdf_url,
    expiresAt: response.expires_at,
  };
}

export default {
  sendTPFMTestEmail,
  getChangeLog,
  getLatestSurchargeSuggestions,
  getToursPricingSuggestions,
  getToursPricingHistory,
  applyToursPricingSuggestion,
  createSurchargeSuggestion,
  getSurchargeJobStatus,
  getDynamicPricingExclusions,
  getDynamicPricingSurchargeEstimates,
  getEligibleSurchargeOffers,
  upsertDynamicPricingExclusion,
  deleteDynamicPricingExclusion,
  getAbandonedCarts,
  clearUserInteractions,
  getUserInteractions,
  getClusters,
  getClusterings,
  getClusterOffers,
  getOfferRecommendations,
  runClustering,
  getAutoYieldStatus,
  listHurdleHeatmaps,
  getHurdleHeatmap,
};
